<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

import { useBountySubmissionStore } from '../../stores/bounty-submission'
import {
  searchLocations,
  reverseGeocodeToAddressDto,
  type NominatimSearchResult
} from '../../composables/useNominatim'
import { getTimezoneFromCoordinates } from '../../utils/timezone'
import { regionConfig } from '@/config/region'

const bountyStore = useBountySubmissionStore()
const { form } = storeToRefs(bountyStore)

const mapContainer = ref<HTMLElement | null>(null)
let mapInstance: L.Map | null = null
let radiusCircle: L.Circle | null = null
let geocodeTimer: ReturnType<typeof setTimeout> | null = null

const isGeocoding = ref(false)
const searchOptions = ref<NominatimSearchResult[]>([])
const selectedSearchResult = ref<NominatimSearchResult | null>(null)

const defaultLat = -25.6101
const defaultLng = 134.3548
const defaultZoom = 5

function initMap() {
  if (!mapContainer.value) return

  const startLat = form.value.location?.lat ?? defaultLat
  const startLng = form.value.location?.lng ?? defaultLng

  const streetLayer = new L.TileLayer(
    'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    { attribution: '&copy; OpenStreetMap contributors &copy; CARTO', maxZoom: 20 }
  )

  const satelliteLayer = new L.TileLayer(
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    {
      attribution:
        'Tiles &copy; Esri &mdash; Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP',
      maxZoom: 20
    }
  )

  mapInstance = new L.Map(mapContainer.value, {
    center: [startLat, startLng],
    zoom: defaultZoom,
    zoomControl: false,
    layers: [satelliteLayer]
  })

  const regionBounds = new L.LatLngBounds(
    [regionConfig.mapBounds.sw.lat, regionConfig.mapBounds.sw.lng],
    [regionConfig.mapBounds.ne.lat, regionConfig.mapBounds.ne.lng]
  )
  mapInstance.setMaxBounds(regionBounds.pad(0.5))

  new L.Control.Layers({ 'Street Map': streetLayer, Satellite: satelliteLayer }, undefined, {
    position: 'topright'
  }).addTo(mapInstance)

  mapInstance.on('moveend', onMoveEnd)

  if (form.value.location) {
    updateRadiusCircle(form.value.location.lat, form.value.location.lng)
  }
}

function updateRadiusCircle(lat: number, lng: number) {
  radiusCircle?.remove()
  if (!mapInstance) return
  radiusCircle = new L.Circle([lat, lng], {
    radius: 100,
    color: '#1565C0',
    weight: 1,
    fillColor: '#1565C0',
    fillOpacity: 0.12
  }).addTo(mapInstance)
}

function onMoveEnd() {
  const center = mapInstance?.getCenter()
  if (!center) return

  const lat = Number(center.lat.toFixed(6))
  const lng = Number(center.lng.toFixed(6))

  updateRadiusCircle(lat, lng)

  isGeocoding.value = true
  if (geocodeTimer) clearTimeout(geocodeTimer)
  geocodeTimer = setTimeout(async () => {
    const timezone = getTimezoneFromCoordinates(lat, lng)
    bountyStore.setLocation(lat, lng, timezone)

    try {
      const dto = await reverseGeocodeToAddressDto(lat, lng)
      bountyStore.setAddress(dto)
    } catch {
      // best-effort
    } finally {
      isGeocoding.value = false
    }
  }, 2000)
}

const onSearchFilter = async (val: string, doneFn: (cb: () => void) => void) => {
  if (!val || val.length < 2) {
    doneFn(() => {
      searchOptions.value = []
    })
    return
  }
  try {
    const results = await searchLocations(val)
    doneFn(() => {
      searchOptions.value = results
    })
  } catch {
    doneFn(() => {
      searchOptions.value = []
    })
  }
}

const onSearchSelect = (result: NominatimSearchResult | null) => {
  if (!result || !mapInstance) return
  const [south, north, west, east] = result.boundingbox.map(Number)
  mapInstance.fitBounds([
    [south, west],
    [north, east]
  ])
  nextTick(() => {
    selectedSearchResult.value = null
  })
}

onMounted(() => {
  initMap()
})

onBeforeUnmount(() => {
  if (geocodeTimer) clearTimeout(geocodeTimer)
  mapInstance?.remove()
  mapInstance = null
})

function validate(): boolean {
  return form.value.location !== null && form.value.address !== null
}

defineExpose({ validate })
</script>

<template>
  <div>
    <h6 class="text-h6 q-mt-none q-mb-xs text-blue-grey-9">Where did it happen?</h6>
    <p class="text-body2 text-blue-grey-8 q-mb-md">
      Search for a location or drag the map to place the pin. The shaded circle shows the 100-metre
      matching area. You will be notified of any footage uploaded within this area and time window.
    </p>

    <div
      class="map-wrapper relative-position rounded-borders overflow-hidden bg-blue-grey-1 q-mb-md"
      style="height: 420px"
    >
      <div class="map-search-overlay">
        <q-select
          v-model="selectedSearchResult"
          :options="searchOptions"
          option-label="display_name"
          use-input
          hide-selected
          fill-input
          input-debounce="500"
          placeholder="Search for a location..."
          dense
          outlined
          bg-color="white"
          class="search-select"
          @filter="onSearchFilter"
          @update:model-value="onSearchSelect"
        >
          <template #prepend>
            <q-icon name="mdi-magnify" size="xs" color="grey-6" />
          </template>
          <template #no-option>
            <q-item>
              <q-item-section class="text-grey">No results</q-item-section>
            </q-item>
          </template>
        </q-select>
      </div>

      <div ref="mapContainer" class="full-height full-width" style="z-index: 10"></div>

      <div
        class="pointer-events-none center-pin-container flex flex-center"
        style="position: absolute; left: 50%; top: 50%; transform: translate(-50%, -100%)"
      >
        <q-icon name="mdi-map-marker" color="blue-8" size="50px" class="pin-drop-shadow" />
        <q-icon
          name="mdi-record-circle-outline"
          color="white"
          size="16px"
          class="absolute-center"
          style="margin-top: -6px"
        />
      </div>
    </div>

    <q-card flat bordered class="border-flat">
      <q-card-section class="q-pa-sm">
        <q-linear-progress v-if="isGeocoding" indeterminate color="blue-8" class="q-mb-xs" />
        <div v-if="form.address" class="text-body2 text-blue-grey-8">
          <q-icon name="mdi-map-marker-check" color="positive" class="q-mr-xs" />
          {{
            form.address.rawInput ||
            [form.address.streetName, form.address.suburb, form.address.state]
              .filter(Boolean)
              .join(', ')
          }}
        </div>
        <div v-else-if="!isGeocoding" class="text-body2 text-blue-grey-5">
          <q-icon name="mdi-map-marker-question" class="q-mr-xs" />
          Pan the map to select a location
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>

<style scoped>
.map-search-overlay {
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1001;
  width: 300px;
}

.search-select {
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.center-pin-container {
  z-index: 500;
}

.pin-drop-shadow {
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.4));
}

.border-flat {
  border: 1px solid var(--q-blue-grey-3);
}
</style>
